/**
 * AppShellLayout Component
 *
 * App shell for all authenticated routes (dashboard, heroes, platform, internal).
 * Wraps every protected route with sidebar, header, CoPilot and Feedback sidebars.
 * Features:
 * - Collapsible sidebar with icon mode, tooltips when collapsed
 * - Sticky header (AppShellHeader) with user controls
 * - CoPilot sidebar (right), Feedback sidebar (right)
 * - Responsive design (tablet-first)
 */

import { ReactNode, useEffect, useMemo } from 'react';
import { Lock, LogOut, TonnieLogo } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { Badge, cn } from '@tonnie/ui';
import {
  ScrollArea,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
} from '@tonnie/ui';
import { Link, useLocation, useSearch } from 'wouter';

import {
  footerNavItems,
  heroNavItems,
  homeNavItem,
  secondaryNavItems,
  standaloneHeroNavItems,
  isNavItemActive,
} from '@/core/config/navigation.config';
import type { INavItem } from '@/core/types/navigation.types';
import { useBetaConfig, useFeatureFlags } from '@tonnie/sdk';
import { BetaBanner } from '@tonnie/ui';
import { useAuth } from '@/core/contexts/AuthContext';
import { useJourneyInstance } from '@/core/contexts/JourneyInstanceContext';
import {
  getFeatureAccess,
  lockedHeroDestination,
  type AccessControlResult,
  type FeatureType,
} from '@/features/platform/auth/utils/planGating';
import { useCoPilotSidebar } from '@/core/contexts/CoPilotSidebarContext';
import { useFeedbackSidebar } from '@/core/contexts/FeedbackSidebarContext';
import { CoPilotLauncher, COPILOT_LAUNCHER_CLEARANCE } from '@tonnie/copilot-ui';
import { CoPilotShell, CoPilotBrain } from '@/features/platform/copilot/components';
import { Feedback } from '@/features/platform/feedback/components';
import { useLogout } from '@/core/hooks';
import { ProfileBasicsHost } from '@/features/secondary/profile-basics';
import { AppShellHeader } from './AppShellHeader';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface IAppShellLayoutProps {
  /** Page content */
  children: ReactNode;
}

// ═══════════════════════════════════════════════════════════════════════
// HELPER
// ═══════════════════════════════════════════════════════════════════════

function getNavTranslationKey(href: string): string {
  const pathToKey: Record<string, string> = {
    '/dashboard': 'nav.home',
    '/checkup': 'nav.checkup',
    '/testit': 'nav.testit',
    '/teleportal': 'nav.teleportal',
    '/qultureup': 'nav.qultureup',
    '/care': 'nav.care',
    '/passport': 'nav.passport',
    '/inout': 'nav.inout',
    '/vault': 'nav.vault',
    '/career': 'nav.career',
    '/family': 'nav.family',
    '/billing': 'nav.billing',
  };
  return pathToKey[href] || href;
}

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export function AppShellLayout({ children }: IAppShellLayoutProps) {
  const { t } = useTranslation();
  const { flags } = useFeatureFlags();
  const [location] = useLocation();
  const { logout } = useLogout({ redirectTo: '/login' });

  const { user } = useAuth();
  const { activeInstance } = useJourneyInstance();
  const { isOpen: isCoPilotOpen, setIsOpen: setCoPilotOpen } = useCoPilotSidebar();
  const { isOpen: isFeedbackOpen, setIsOpen: setFeedbackOpen } = useFeedbackSidebar();
  const { data: betaConfig } = useBetaConfig();

  // Pre-compute access levels for hero nav items
  const heroAccessMap = useMemo(() => {
    const map: Record<string, AccessControlResult> = {};
    heroNavItems.forEach((item) => {
      if (item.featureType && user) {
        map[item.href] = getFeatureAccess(
          user,
          item.featureType as FeatureType,
          activeInstance ?? undefined
        );
      }
    });
    return map;
  }, [user, activeInstance]);

  useEffect(() => {
    if (isCoPilotOpen && isFeedbackOpen) {
      setFeedbackOpen(false);
    }
  }, [isCoPilotOpen, isFeedbackOpen, setFeedbackOpen]);

  useEffect(() => {
    if (isFeedbackOpen && isCoPilotOpen) {
      setCoPilotOpen(false);
    }
  }, [isFeedbackOpen, isCoPilotOpen, setCoPilotOpen]);

  const search = useSearch();
  const isActive = (href: string) => isNavItemActive(location, search, href);

  const accessoryNavItems = secondaryNavItems.filter((item) => {
    if (item.href === '/family') return flags.FAMILY_ADDON;
    if (item.href === '/career') return flags.CAREER;
    return true;
  });

  /** An item with no plan gate: its name, its icon, its page. */
  const renderPlainItem = (item: INavItem) => {
    const key = item.translationKey || getNavTranslationKey(item.href);
    return (
      <SidebarMenuItem key={`${item.href}-${item.label}`}>
        <SidebarMenuButton
          asChild
          isActive={isActive(item.href)}
          tooltip={t(key)}
          className="group-data-[collapsible=icon]:justify-center"
        >
          <Link href={item.href}>
            <item.icon className="h-[22px] w-[22px] group-data-[collapsible=icon]:h-6 group-data-[collapsible=icon]:w-6 shrink-0" />
            <span>{t(key)}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <SidebarProvider defaultOpen={true}>
      <Sidebar collapsible="icon" data-testid="app-sidebar">
        <SidebarHeader className="h-16 shrink-0 border-b border-sidebar-border justify-center items-center p-0">
          <div className="flex items-center justify-center h-full w-full px-2">
            <TonnieLogo
              variant="symbol"
              size="sm"
              className="shrink-0 group-data-[collapsible=icon]:w-7 group-data-[collapsible=icon]:h-7"
            />
            <div className="flex items-center gap-2 ml-3 group-data-[collapsible=icon]:hidden">
              <span className="font-bold text-xl tracking-tight text-sidebar-foreground">
                {t('common.appShortName')}
              </span>
            </div>
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarMenu className="group-data-[collapsible=icon]:items-center mt-3">
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isActive(homeNavItem.href)}
                tooltip={t(getNavTranslationKey(homeNavItem.href))}
                className="group-data-[collapsible=icon]:justify-center"
              >
                <Link href={homeNavItem.href}>
                  <homeNavItem.icon className="h-[22px] w-[22px] group-data-[collapsible=icon]:h-6 group-data-[collapsible=icon]:w-6 shrink-0" />
                  <span>{t(getNavTranslationKey(homeNavItem.href))}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>

          <SidebarSeparator />

          <SidebarMenu className="group-data-[collapsible=icon]:items-center">
            {heroNavItems.map((item) => {
              const access = heroAccessMap[item.href];
              const isLocked = access && !access.canAccess;
              const isLimited = access?.canAccess && access.accessLevel === 'limited';
              const href = !isLocked
                ? item.href
                : lockedHeroDestination(access) === 'billing'
                  ? '/billing'
                  : homeNavItem.href;

              return (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.href)}
                    tooltip={
                      isLocked
                        ? `${t(getNavTranslationKey(item.href))} (${t('accessControl.locked')})`
                        : t(getNavTranslationKey(item.href))
                    }
                    className="group-data-[collapsible=icon]:justify-center"
                  >
                    <Link href={href}>
                      <item.icon className="h-[22px] w-[22px] group-data-[collapsible=icon]:h-6 group-data-[collapsible=icon]:w-6 shrink-0" />
                      <span
                        className="flex items-center gap-1.5"
                        data-testid={`hero-nav-${item.href.replace(/^\//, '')}`}
                      >
                        {t(getNavTranslationKey(item.href))}
                        {isLocked && (
                          <Lock
                            className="h-3 w-3 text-muted-foreground"
                            data-testid="hero-nav-lock"
                          />
                        )}
                        {/*
                          Rendered only for `accessLevel === 'limited'`. CheckUp
                          used to land here on a free journey — the badge was a
                          faithful rendering of a wrong verdict, not a bug of
                          its own — and the fix is upstream, in plan-gating's
                          FREE_HERO_FEATURES. The testid exists so the E2E can
                          assert the badge's ABSENCE structurally; asserting on
                          a CSS class would be meaningless here, since @tonnie/ui
                          primitives carry variant classes unconditionally.
                        */}
                        {isLimited && (
                          <Badge
                            variant="outline"
                            className="text-2xs px-1 py-0 h-4 leading-none font-normal"
                            data-testid="hero-nav-basic-badge"
                          >
                            {t('common.plan.basic')}
                          </Badge>
                        )}
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>

          <SidebarSeparator />

          {/* The heroes of the second line — used without a journey. */}
          <SidebarMenu
            className="group-data-[collapsible=icon]:items-center"
            data-testid="nav-standalone-heroes"
          >
            {standaloneHeroNavItems
              .filter((item) => item.href !== '/inout' || flags.INOUT_ENABLED)
              .map(renderPlainItem)}
          </SidebarMenu>

          {/* The accessory pages, each behind its flag — no group when none is on. */}
          {accessoryNavItems.length > 0 && (
            <>
              <SidebarSeparator />
              <SidebarMenu className="group-data-[collapsible=icon]:items-center">
                {accessoryNavItems.map(renderPlainItem)}
              </SidebarMenu>
            </>
          )}

          <SidebarSeparator />
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu className="group-data-[collapsible=icon]:items-center">
            {footerNavItems.map((item) => {
              const active = isActive(item.href);
              return (
                <SidebarMenuItem key={`footer-${item.href}`}>
                  <SidebarMenuButton
                    asChild
                    isActive={active}
                    tooltip={t(item.translationKey || getNavTranslationKey(item.href))}
                    className="group-data-[collapsible=icon]:justify-center"
                  >
                    <Link href={item.href}>
                      <item.icon className="h-5 w-5 group-data-[collapsible=icon]:h-6 group-data-[collapsible=icon]:w-6" />
                      <span>{t(item.translationKey || getNavTranslationKey(item.href))}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
          {/* Separator must NOT be a direct child of a menu <ul> (axe `list`
              requires <ul> to only contain <li>). Place it between two
              SidebarMenu blocks — same pattern as the nav sections above. */}
          <SidebarSeparator />
          <SidebarMenu className="group-data-[collapsible=icon]:items-center">
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={logout}
                className="text-destructive-accessible hover:text-destructive-accessible hover:bg-destructive/10 group-data-[collapsible=icon]:justify-center"
                tooltip={t('common.logout')}
              >
                <LogOut className="h-5 w-5 group-data-[collapsible=icon]:h-6 group-data-[collapsible=icon]:w-6" />
                <span>{t('common.logout')}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>

        <SidebarRail toggleLabel={t('ui.sidebar.toggle')} />
      </Sidebar>

      <SidebarInset>
        <AppShellHeader />
        {/* BL-022 / BL-055: PERMANENT beta banner — server-driven via
            /api/config/beta, no dismiss control (every Tonnie app always shows
            the beta notice; only the server flag hides it). CTA opens the
            Feedback drawer. */}
        {betaConfig?.beta.enabled && (
          <BetaBanner
            message={t('common.beta.bannerMessage')}
            ctaLabel={t('common.beta.bannerCta')}
            onCtaClick={() => setFeedbackOpen(true)}
          />
        )}
        <ScrollArea className="flex-1 min-h-0">
          <div
            className={cn(
              'container pt-8 max-w-7xl mx-auto overflow-hidden',
              // Clear the Ask Tonnie launcher so it never covers a page's last controls.
              COPILOT_LAUNCHER_CLEARANCE
            )}
          >
            {children}
          </div>
        </ScrollArea>
      </SidebarInset>

      <CoPilotShell isOpen={isCoPilotOpen} onClose={() => setCoPilotOpen(false)} showBeta>
        <CoPilotBrain />
      </CoPilotShell>
      {/* Floating "Ask Tonnie" launcher (BL-217) — discoverable entry point;
          hides itself while the panel is open. */}
      <CoPilotLauncher open={isCoPilotOpen} onOpen={() => setCoPilotOpen(true)} />
      <Feedback />
      {/* Your basics — a window over any page, opened by `?basics=…`. */}
      <ProfileBasicsHost />
    </SidebarProvider>
  );
}

export default AppShellLayout;
